<!-- src/apps/secret/views/docs/ApiEndpoint.vue -->

<script setup lang="ts">
  /**
   * A single API operation, rendered as an expandable terminal row.
   *
   * Collapsed: method badge · path · summary · auth marker (+ deprecated).
   * Expanded: description, path/query parameters, request body schema and the
   * 200 response schema. Schemas are rendered lazily (only once expanded) via
   * JsonSchemaView so the big specs don't inflate the DOM up front.
   */
  import { computed, ref } from 'vue';
  import { useI18n } from 'vue-i18n';

  import JsonSchemaView from './JsonSchemaView.vue';

  type Schema = Record<string, unknown>;
  type Operation = {
    operationId?: string;
    summary?: string;
    description?: string;
    deprecated?: boolean;
    security?: Array<Record<string, unknown>>;
    parameters?: Array<{
      name: string;
      in: string;
      required?: boolean;
      description?: string;
      schema?: Schema;
    }>;
    requestBody?: {
      content?: Record<string, { schema?: Schema }>;
    };
    responses?: Record<string, { description?: string; content?: Record<string, { schema?: Schema }> }>;
  };

  const props = defineProps<{
    path: string;
    method: string;
    operation: Operation;
  }>();

  const { t } = useI18n();

  const expanded = ref(false);

  const methodUpper = computed(() => props.method.toUpperCase());

  // Method → colour. Uses the shared brand blue for POST and default-palette
  // hues for the rest; every pair is legible in both themes at AA.
  const methodClass = computed(() => {
    switch (methodUpper.value) {
      case 'GET':
        return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300';
      case 'POST':
        return 'bg-brand-50 text-brand-700 dark:bg-brand-500/10 dark:text-brand-300';
      case 'PUT':
        return 'bg-violet-50 text-violet-700 dark:bg-violet-500/10 dark:text-violet-300';
      case 'PATCH':
        return 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300';
      case 'DELETE':
        return 'bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-300';
      default:
        return 'bg-day-100 text-day-ink-muted dark:bg-night-100 dark:text-night-ink-muted';
    }
  });

  // Security in OpenAPI is an OR of alternatives: any one satisfies auth. An
  // empty `{}` alternative means "no credentials needed", so the endpoint is
  // publicly reachable whenever ANY alternative is empty. Auth is therefore
  // required only when EVERY alternative is non-empty — otherwise the read
  // endpoints the generator marks as `[{basicAuth:[]}, {}]` would be
  // mislabeled "Auth required" when they are in fact callable anonymously.
  const requiresAuth = computed(() => {
    const sec = props.operation.security;
    return (
      Array.isArray(sec) &&
      sec.length > 0 &&
      sec.every((s) => s && Object.keys(s).length > 0)
    );
  });

  const parameters = computed(() => props.operation.parameters ?? []);

  // Prefer the JSON body — it is the shape most integrators need — even when
  // another media type (e.g. multipart/form-data) is listed first.
  const requestBody = computed(() => {
    const content = props.operation.requestBody?.content;
    if (!content) return null;
    const mediaType = 'application/json' in content ? 'application/json' : Object.keys(content)[0];
    if (!mediaType) return null;
    return { mediaType, schema: content[mediaType]?.schema as Schema | undefined };
  });

  // Any 2xx counts as the success response (not just 200/201): pick the first
  // 2xx that documents a JSON schema, else the first 2xx (e.g. a bare 204) so
  // the section still renders its status + description.
  const successResponse = computed(() => {
    const responses = props.operation.responses ?? {};
    const codes = Object.keys(responses)
      .filter((c) => /^2\d\d$/.test(c))
      .sort();
    if (!codes.length) return null;
    const code = codes.find((c) => responses[c]?.content?.['application/json']?.schema) ?? codes[0];
    const entry = responses[code];
    const schema = entry?.content?.['application/json']?.schema as Schema | undefined;
    return { code, description: entry?.description, schema };
  });

  const summaryText = computed(
    () => props.operation.summary || props.operation.operationId || props.path
  );
</script>

<template>
  <div class="border-t border-day-line first:border-t-0 dark:border-white/[0.07]">
    <button
      type="button"
      class="flex w-full items-start gap-3 px-3 py-2.5 text-left transition-colors
        hover:bg-day-100/70 dark:hover:bg-night-100/60"
      :aria-expanded="expanded"
      @click="expanded = !expanded">
      <span
        class="mt-0.5 inline-block shrink-0 rounded-sm px-1.5 py-0.5 font-mono text-[10px]
          font-semibold tracking-[0.04em] uppercase"
        :class="methodClass">
        {{ methodUpper }}
      </span>

      <span class="min-w-0 flex-1">
        <span class="block font-mono text-[12.5px] break-all text-day-ink dark:text-night-ink">
          {{ path }}
        </span>
        <span class="mt-0.5 block text-[12px] text-day-ink-muted dark:text-night-ink-muted">
          {{ summaryText }}
        </span>
      </span>

      <span class="flex shrink-0 items-center gap-2">
        <span
          v-if="operation.deprecated"
          class="hidden font-mono text-[9px] tracking-[0.08em] text-amber-600 uppercase sm:inline dark:text-amber-400">
          {{ t('web.docs.deprecated') }}
        </span>
        <span
          v-if="requiresAuth"
          class="hidden font-mono text-[9px] tracking-[0.08em] text-day-ink-faint uppercase sm:inline dark:text-night-ink-faint"
          :title="t('web.docs.auth_required')">
          {{ t('web.docs.auth_required') }}
        </span>
        <span
          v-else
          class="hidden font-mono text-[9px] tracking-[0.08em] text-emerald-600 uppercase sm:inline dark:text-emerald-400">
          {{ t('web.docs.public') }}
        </span>
        <svg
          class="size-3.5 shrink-0 text-day-ink-faint transition-transform dark:text-night-ink-faint"
          :class="expanded ? 'rotate-180' : ''"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </span>
    </button>

    <div
      v-if="expanded"
      class="space-y-5 border-t border-day-line bg-day-100/40 px-3 py-4
        dark:border-white/[0.07] dark:bg-night-100/30">
      <!-- Auth marker (mobile: hidden in the header, shown here) -->
      <div class="flex flex-wrap items-center gap-2 sm:hidden">
        <span
          v-if="requiresAuth"
          class="font-mono text-[9px] tracking-[0.08em] text-day-ink-faint uppercase dark:text-night-ink-faint">
          {{ t('web.docs.auth_required') }}
        </span>
        <span
          v-else
          class="font-mono text-[9px] tracking-[0.08em] text-emerald-600 uppercase dark:text-emerald-400">
          {{ t('web.docs.public') }}
        </span>
      </div>

      <p
        v-if="operation.description"
        class="text-[13px] leading-relaxed text-day-ink-muted dark:text-night-ink-muted">
        {{ operation.description }}
      </p>

      <!-- Parameters -->
      <section v-if="parameters.length">
        <h4 class="mb-2 font-mono text-[10px] font-semibold tracking-[0.1em] text-day-ink-faint uppercase dark:text-night-ink-faint">
          {{ t('web.docs.parameters') }}
        </h4>
        <ul class="space-y-2">
          <li
            v-for="param in parameters"
            :key="param.in + ':' + param.name"
            class="border-l border-day-line pl-3 dark:border-white/[0.09]">
            <div class="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <span class="font-mono text-[12px] text-day-ink dark:text-night-ink">{{ param.name }}</span>
              <span class="font-mono text-[10px] text-day-ink-faint dark:text-night-ink-faint">{{ param.in }}</span>
              <span
                v-if="param.required"
                class="font-mono text-[9px] tracking-[0.08em] text-rose-600 uppercase dark:text-rose-400">
                {{ t('web.docs.required') }}
              </span>
            </div>
            <p
              v-if="param.description"
              class="mt-0.5 text-[12px] leading-relaxed text-day-ink-muted dark:text-night-ink-muted">
              {{ param.description }}
            </p>
          </li>
        </ul>
      </section>

      <!-- Request body -->
      <section v-if="requestBody && requestBody.schema">
        <h4 class="mb-2 flex flex-wrap items-baseline gap-2 font-mono text-[10px] font-semibold tracking-[0.1em] text-day-ink-faint uppercase dark:text-night-ink-faint">
          {{ t('web.docs.request_body') }}
          <span class="font-normal tracking-normal text-day-ink-faint normal-case dark:text-night-ink-faint">
            {{ requestBody.mediaType }}
          </span>
        </h4>
        <div class="overflow-x-auto">
          <JsonSchemaView :schema="requestBody.schema" />
        </div>
      </section>

      <!-- Response -->
      <section v-if="successResponse">
        <h4 class="mb-2 flex flex-wrap items-baseline gap-2 font-mono text-[10px] font-semibold tracking-[0.1em] text-day-ink-faint uppercase dark:text-night-ink-faint">
          {{ t('web.docs.response') }}
          <span class="font-normal tracking-normal text-day-ink-faint dark:text-night-ink-faint">
            {{ successResponse.code }}
          </span>
        </h4>
        <div
          v-if="successResponse.schema"
          class="overflow-x-auto">
          <JsonSchemaView :schema="successResponse.schema" />
        </div>
        <p
          v-else
          class="text-[12px] leading-relaxed text-day-ink-muted dark:text-night-ink-muted">
          {{ successResponse.description || t('web.docs.no_response_body') }}
        </p>
      </section>
    </div>
  </div>
</template>
